import { DeliveryMethodType } from "@/store/slices/delivery.slice";
import { DeliveryPoint } from "@/store/thunks/delivery.thunks";
import { OrderResponse } from "@/store/types/order";

export enum CheckoutStep {
  BANK_ID = "BANK_ID",
  DELIVERY = "DELIVERY",
  PAYMENT = "PAYMENT",
}

export interface BankIdData {
  name: string;
  personalNumber: string;
  email?: string;
  phone?: string;
}

export interface DeliveryData {
  deliveryMethod: DeliveryMethodType;
  selectedServicePoint: DeliveryPoint | null;
  selectedParcelBox: DeliveryPoint | null;
  address?: {
    street: string;
    city: string;
    postalCode: string;
  };
}

export interface PaymentData {
  orderId: string;
}

// Map step types to their data
export type StepDataMap = {
  [CheckoutStep.BANK_ID]: BankIdData;
  [CheckoutStep.DELIVERY]: DeliveryData;
  [CheckoutStep.PAYMENT]: PaymentData;
};

// Generic type for step state
export type StepState<T extends CheckoutStep> = {
  completed: boolean;
  data: StepDataMap[T];
};

export interface BaseStepProps<T extends CheckoutStep> {
  isActive: boolean;
  onComplete: (data: StepDataMap[T]) => void;
  onEdit?: () => void;
}

// Props specifically for Payment step
export interface PaymentStepProps extends BaseStepProps<CheckoutStep.PAYMENT> {
  order: OrderResponse;
}

// Generic props interface for step components
export interface StepProps<T extends CheckoutStep> {
  isActive: boolean;
  isCompleted: boolean;
  onComplete: (data: StepDataMap[T]) => void;
  onEdit?: () => void;
}
// Helper type to create component types for each step
export type StepComponentType<T extends CheckoutStep> = React.ComponentType<
  StepProps<T>
>;

// Config type that ensures component matches the step type
export type StepConfig<T extends CheckoutStep> = {
  id: T;
  title: string;
  component: StepComponentType<T>;
  completedComponent?: React.ComponentType<{ data: StepDataMap[T] }>;
};

// Type for the array of step configs
export type CheckoutStepConfig = {
  [K in CheckoutStep]: StepConfig<K>;
}[CheckoutStep];
